import { Link } from "@tanstack/react-router";
import { Facebook } from "lucide-react";
import { CookieSettingsButton } from "@/components/cookie-consent";
import { LanguageSwitcher } from "@/components/language-switcher";
import { BRAND } from "@/lib/behot";
import { useT } from "@/i18n";

function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function SiteFooter() {
  const { t } = useT();

  return (
    <footer className="mt-20 border-t border-border bg-card">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:grid-cols-2 md:grid-cols-4">
        <div>
          <Link to="/" className="flex items-center gap-2" aria-label={t("nav.home")}>
            <picture>
              <source srcSet="/brand/icon-96.webp 96w, /brand/icon-192.webp 192w" sizes="44px" type="image/webp" />
              <img
                src="/brand/icon-96.png"
                srcSet="/brand/icon-96.png 96w, /brand/icon-192.png 192w"
                sizes="44px"
                width={44}
                height={44}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-10 w-10 sm:h-11 sm:w-11"
              />
            </picture>
            <span className="font-display text-xl font-extrabold tracking-tight sm:text-2xl">
              <span className="text-gradient-hot">BeHot</span>
              <span className="text-foreground">.Now</span>
            </span>
          </Link>
          <p className="mt-4 max-w-sm text-sm text-muted-foreground">
            {t("footer.tagline")}
          </p>
          <Link
            to="/how-it-works"
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            {t("footer.howItWorks")}
          </Link>
          <p className="mt-4 text-xs text-muted-foreground">
            {t("footer.operatedBy")}{" "}
            <a href={BRAND.hrnUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              {BRAND.operator}
            </a>
            . {t("footer.disclosure")}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <a href="https://maidensail.com/startup/behot-now" target="_blank" rel="noopener noreferrer dofollow" aria-label="Featured on Maidensail">
              <img src="https://maidensail.com/badge/behot-now.svg" alt="Featured on Maidensail" height={44} width={160} loading="lazy" decoding="async" className="h-11 w-auto rounded-lg border border-border bg-card" />
            </a>
            <a href="https://nicklaunches.com/products/behot/?utm_source=behot.now&utm_medium=badge&utm_campaign=featured-premium" target="_blank" rel="noopener noreferrer" aria-label="BeHot on Nick Launches">
              <img src="https://nicklaunches.com/badges/featured-premium-dark.png" alt="BeHot on Nick Launches" width={244} height={56} loading="lazy" decoding="async" className="h-11 w-auto rounded-lg border border-border bg-card" />
            </a>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <a
              href="https://x.com/BeHotNow2026"
              target="_blank"
              rel="noopener noreferrer nofollow"
              aria-label="BeHot.Now on X"
              title="BeHot.Now on X"
              className="flex size-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <XLogo className="size-4" />
            </a>
            <a
              href="https://facebook.com/BeHotNow"
              target="_blank"
              rel="noopener noreferrer nofollow"
              aria-label="BeHot.Now on Facebook"
              title="BeHot.Now on Facebook"
              className="flex size-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <Facebook className="size-4" />
            </a>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold">{t("footer.platform")}</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li><Link to="/hot" className="hover:text-foreground">{t("footer.hotNow")}</Link></li>
            <li><Link to="/history" className="hover:text-foreground">{t("footer.hotHistory")}</Link></li>
            <li><Link to="/marketplace" className="hover:text-foreground">{t("nav.marketplace")}</Link></li>
            <li><Link to="/categories" className="hover:text-foreground">{t("nav.categories")}</Link></li>
            <li><Link to="/get-hot" className="hover:text-foreground">{t("footer.getHot")}</Link></li>
            <li><Link to="/referrals" className="hover:text-foreground">{t("footer.referrals")}</Link></li>
            <li><Link to="/earn" className="hover:text-foreground">{t("nav.earn")}</Link></li>
            <li><Link to="/withdrawal" className="hover:text-foreground">{t("footer.withdrawal")}</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold">{t("footer.comingSoon")}</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li><Link to="/fund" className="hover:text-foreground">{t("nav.fund")}</Link></li>
            <li><Link to="/promote" className="hover:text-foreground">{t("nav.promote")}</Link></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold">{t("footer.legal")}</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li><Link to="/terms" className="hover:text-foreground">{t("footer.terms")}</Link></li>
            <li><Link to="/privacy" className="hover:text-foreground">{t("footer.privacy")}</Link></li>
            <li><Link to="/cookie" className="hover:text-foreground">{t("footer.cookie")}</Link></li>
            <li>
              <CookieSettingsButton className="hover:text-foreground" />
            </li>
          </ul>

          <h3 className="mt-6 text-sm font-semibold">{t("lang.select")}</h3>
          <div className="mt-2 flex items-center gap-2">
            <LanguageSwitcher className="border border-border" />
            <span className="text-sm text-muted-foreground">{t("lang.change")}</span>
          </div>
        </div>
      </div>
      <div className="border-t border-border py-5 text-center">
        <nav className="mb-2 flex flex-wrap items-center justify-center gap-x-1 text-xs text-muted-foreground">
          <Link to="/marketplace/guides" className="hover:text-foreground">{t("mpseo.hub.h1")}</Link>
          <span>·</span>
          <Link to="/compare/marketplace" className="hover:text-foreground">{t("mpseo.compareOthers")}</Link>
          <span>·</span>
          <Link to="/compare/hot" className="hover:text-foreground">{t("footer.compareLeaderboards")}</Link>
        </nav>
        <p className="text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()}{" "}
          <a href={BRAND.hrnUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
            {BRAND.operator}
          </a>
          . {t("footer.rights")}
        </p>
      </div>

    </footer>
  );
}
